import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sortByLabel, sortByName, sortStrings } from "@/lib/sortRows";
import { roleLabel } from "@/lib/roleLabels";
import AppLayout from "@/components/AppLayout";
import { StatusBadge, PriorityBadge, StatusSelectPill, STATUS_STYLES } from "@/components/StatusBadge";
import AvatarStack from "@/components/AvatarStack";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PagedList from "@/components/PagedList";
import RequesterPicker from "@/components/RequesterPicker";
import SlippageCell from "@/components/SlippageCell";
import { DONE_TASK_STATUSES, matchesSchedule, SCHEDULE_FILTERS, slippageSortValue, targetDate } from "@/lib/ageing";
import TableSearch, { matchesQuery } from "@/components/TableSearch";
import { PlusIcon, LinkSimpleIcon, ArrowLeftIcon, ArrowSquareOutIcon, SparkleIcon, ShieldWarningIcon, XIcon, CheckCircleIcon, MagnifyingGlassIcon, WarningCircleIcon, TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { prettyUrl } from "@/lib/prettyUrl";
import ReportIssueModal from "@/components/ReportIssueModal";

const P_STATUSES = ["initiation", "planning", "execution", "closed", "hold"];
const P_PRIORITIES = ["high", "medium", "low"];
// The task_status enum, exactly: Open -> WIP -> Test -> UAT -> Cutover -> Live,
// with Hold as a side-state at any point. Live is the terminal "shipped" state.
// "closed" is the terminal administrative state — finished with, as opposed to
// shipped — and was added to the enum itself by
// supabase/schema_task_status_closed.sql. It is NOT a frontend-only label: the
// Postgres task_status type has to carry it, or every status change to Closed
// comes back as "invalid input value for enum task_status" behind a generic
// "something went wrong" toast (which is exactly what happened when this list
// offered Closed before the enum had it). Keep this list, the enum, and the
// ai-chat edge function's list_tasks status enum in step.
const T_STATUSES = ["open", "wip", "test", "uat", "cutover", "live", "closed", "cancelled", "hold"];
const T_PRIORITIES = ["low", "medium", "high", "urgent"];

// A date column, or an em dash when there is nothing to show. Same shape the
// Tasks page uses, so the two grids read identically.
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// live = shipped, closed = finished with, cancelled = dropped. All three stop
// the clock; only cancelled has no delivery to be early or late for. Same
// definition TasksPage.jsx uses, so the schedule filter reads identically.
const taskDone = (t) => DONE_TASK_STATUSES.includes(t.status);

// timestamptz -> yyyy-mm-dd for a plain <input type="date">
function toDateInput(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function GapAssignButton({ gap, users, projectId, onAssigned }) {
  const [uid, setUid] = useState("");
  const [busy, setBusy] = useState(false);
  const assign = async () => {
    if (!uid) return;
    setBusy(true);
    try {
      await api.post(`/onboarding/skill-path/${uid}/items`, {
        skill: gap.skill,
        target_level: gap.needed_level || "moderate",
        why: `Required for project skill gap: ${gap.evidence || gap.skill}`,
        evidence: gap.evidence || "",
        phase: "60 days",
        source_project_id: projectId,
      });
      onAssigned && onAssigned(uid);
      setUid("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1" data-testid={`gap-assign-${gap.skill}`}>
      <Select value={uid} onValueChange={setUid}>
        <SelectTrigger className="rounded-sm h-7 text-[10px] w-[120px]" data-testid={`gap-assign-select-${gap.skill}`}>
          <SelectValue placeholder="Assign to…" />
        </SelectTrigger>
        <SelectContent>
          {users.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <button
        disabled={!uid || busy}
        onClick={assign}
        data-testid={`gap-assign-btn-${gap.skill}`}
        className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider rounded-sm border border-[#0176D3] bg-[#0176D3] text-white hover:bg-[#005FB2] disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? "…" : "Add"}
      </button>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [addMemberId, setAddMemberId] = useState("");
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", status: "open", priority: "medium", assignee_ids: [], attachment_url: "", eta: "", revised_eta: "", requested_by: "" });
  // Guards createTask against double-clicks / double-Enter — the dialog
  // stays open for the whole round-trip to Supabase, so without this the
  // Create button was clickable again before the first insert resolved,
  // firing a second identical POST /tasks and creating a duplicate row.
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({ title: "", url: "" });
  const [reportLoading, setReportLoading] = useState(false);
  // Filters the New Task assignee checklist. The roster can run to dozens of
  // people, and scrolling a 40-row list to find one name is worse than typing
  // three letters of it.
  const [assigneeQuery, setAssigneeQuery] = useState("");
  // Filters for the Tasks tab's own grid — same shape as TasksPage.jsx's
  // (status/priority/assignee/schedule/search) so the two views behave
  // identically, scoped down to just this project's tasks.
  const [taskQuery, setTaskQuery] = useState("");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [taskPriorityFilter, setTaskPriorityFilter] = useState("all");
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState("all");
  const [taskScheduleFilter, setTaskScheduleFilter] = useState("all");
  // Same ETA/revised-ETA window as the global Tasks page — the NVL(revised,
  // eta) target date, so a task with a revised ETA is filtered by that, not
  // its superseded original one.
  const [taskEtaFrom, setTaskEtaFrom] = useState("");
  const [taskEtaTo, setTaskEtaTo] = useState("");
  const [skillGaps, setSkillGaps] = useState(null); // {required_skills, gaps, team_size}
  const [gapsLoading, setGapsLoading] = useState(false);

  const isAdmin = currentUser?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere — see
  // is_manager_or_admin() in schema_owner_role.sql.
  const isManager = currentUser?.role === "manager" || currentUser?.role === "owner";

  const load = async () => {
    const [p, t, u, m] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/tasks?project_id=${id}`),
      api.get("/users"),
      api.get(`/projects/${id}/members`),
    ]);
    setProject(p.data);
    setEditForm({
      title: p.data.title, description: p.data.description || "", plan: p.data.plan || "",
      status: p.data.status, initiative_id: p.data.initiative_id,
      priority: p.data.priority || "medium", owner_id: p.data.owner_id || "",
      start_date: toDateInput(p.data.start_date), end_date: toDateInput(p.data.end_date),
      entity: p.data.entity || "", task_number: p.data.task_number || "",
      risk_challenges: p.data.risk_challenges || "", tech_team: p.data.tech_team || "",
      func_team: p.data.func_team || "", revised_eta: toDateInput(p.data.revised_eta),
      fy_lic_cost: p.data.fy_lic_cost ?? "", imp_cost: p.data.imp_cost ?? "",
      comments: p.data.comments || "",
    });
    setTasks(t.data);
    setUsers(sortByName(u.data));
    setMembers(sortByName(m.data));
  };
  useEffect(() => { load(); }, [id]);

  // Ownership/admin/manager can manage the roster and edit any task; a
  // plain member can only ever update a task they're personally assigned
  // to (mirrors the "update own or manager tasks" RLS policy exactly —
  // this just keeps the UI from offering a control that would fail).
  const isOwner = project?.owner_id === currentUser?.id;
  const canManageMembers = isAdmin || isManager || isOwner;
  const canEditTask = (t) => isAdmin || isManager || (t.assignee_ids || [t.assignee_id]).includes(currentUser?.id);

  const memberIds = new Set(members.map((m) => m.user_id));
  const addableUsers = users.filter((u) => u._id !== project?.owner_id && !memberIds.has(u._id));

  // A task can only be assigned to someone actually on the project: the owner
  // (who always has access per the "read projects"/"read tasks" RLS policies)
  // plus the Members roster. Offering the whole directory here produced tasks
  // assigned to people who then couldn't open them.
  const projectPeople = users.filter((u) => u._id === project?.owner_id || memberIds.has(u._id));

  const assigneeMatches = projectPeople.filter((u) => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return true;
    return `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q);
  });

  const addMember = async () => {
    if (!addMemberId) return;
    try {
      await api.post(`/projects/${id}/members`, { user_id: addMemberId });
      setAddMemberId("");
      toast.success("Member added");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || e.message); }
  };

  const saveProject = async () => {
    try {
      await api.patch(`/projects/${id}`, {
        ...editForm,
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        revised_eta: editForm.revised_eta || null,
        fy_lic_cost: editForm.fy_lic_cost === "" ? null : Number(editForm.fy_lic_cost),
        imp_cost: editForm.imp_cost === "" ? null : Number(editForm.imp_cost),
      });
      toast.success("Updated");
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const toggleTaskAssignee = (uid) => {
    setTaskForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(uid) ? f.assignee_ids.filter((x) => x !== uid) : [...f.assignee_ids, uid],
    }));
  };

  const createTask = async () => {
    if (!taskForm.title.trim()) { toast.error("Title required"); return; }
    /*
      One assignee, minimum.

      An unassigned task counts towards nobody's workload, appears in no
      member report and lands in no digest, so it is work that exists only on
      this grid. The database refuses the insert as well
      (trg_require_task_assignee in supabase/schema_revised_eta_reason.sql) —
      this check just says so before the round trip.
    */
    if (taskForm.assignee_ids.length === 0) {
      toast.error("Pick at least one assignee.");
      return;
    }
    /*
      An ETA and a requester, both required at creation.

      The ETA is the commitment everything else is measured against -- and it
      freezes the moment it is set (schema_task_eta_lock.sql), so a task created
      without one can never acquire a baseline at all. The requester is who the
      work is answerable to. Both are enforced by
      trg_require_task_creation_fields as well; these checks exist to name the
      missing field before the round trip.
    */
    if (!taskForm.eta) {
      toast.error("Set an ETA — it is the date this task is committed to.");
      return;
    }
    if (!taskForm.requested_by.trim()) {
      toast.error("Say who requested this task.");
      return;
    }
    if (taskSubmitting) return;
    setTaskSubmitting(true);
    try {
      const [primary, ...rest] = taskForm.assignee_ids;
      const { title, description, status, priority, attachment_url, eta, revised_eta, requested_by } = taskForm;
      const { data: created } = await api.post("/tasks", {
        title, description, status, priority, project_id: id, assignee_id: primary || null,
        requested_by: requested_by.trim(),
        // eta/revised_eta are timestamptz columns — an empty string (the
        // input's default) fails Postgres's date parsing outright, so it
        // has to become a real null, same as due_date elsewhere.
        attachment_url: attachment_url.trim() || null,
        eta: eta || null,
        revised_eta: revised_eta || null,
      });
      // Additional assignees beyond the primary go through task_assignees —
      // tasks.assignee_id only ever holds one person.
      for (const uid of rest) {
        await api.post(`/tasks/${created._id}/assignees`, { user_id: uid });
      }
      toast.success("Task created");
      setTaskForm({ title: "", description: "", status: "open", priority: "medium", assignee_ids: [], attachment_url: "", eta: "", revised_eta: "", requested_by: "" });
      setAssigneeQuery("");
      setTaskOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setTaskSubmitting(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const deleteProject = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      nav("/projects");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setDeleting(false);
    }
  };

  const checkSkillCoverage = async () => {
    setGapsLoading(true);
    setSkillGaps(null);
    try {
      const { data } = await api.get(`/projects/${id}/skill-gaps`);
      setSkillGaps(data);
      if ((data.gaps || []).length === 0) toast.success("Team fully covers the required skills");
      else toast.warning(`${data.gaps.length} skill gap(s) detected`);
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setGapsLoading(false); }
  };

  const updateTask = async (task, patch) => {
    // Only the changed fields — task itself now carries a derived
    // assignee_ids array that isn't a real column; spreading the whole
    // task back into the PATCH body would send that straight to Postgres.
    try {
      await api.patch(`/tasks/${task._id}`, patch);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const addDoc = async () => {
    if (!docForm.title.trim() || !docForm.url.trim()) { toast.error("Title and URL required"); return; }
    try {
      await api.post(`/projects/${id}/documents`, docForm);
      toast.success("Document added");
      setDocForm({ title: "", url: "" });
      setDocOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  // Opens the standalone, no-login report view (/r/:token -> view-report edge
  // function) in a new tab, rather than navigating to /reports in-app: that
  // page shows the whole Reports hub around it, while this button is meant to
  // show ONLY the generated report. Every report row already carries a
  // share_token (schema_report_share_token.sql, defaulted at the database
  // level), so no extra step is needed to make it shareable before opening it.
  const generateReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.post("/reports/generate", { scope: "project", target_id: id });
      toast.success("Report generated");
      window.open(`${window.location.origin}/r/${data.share_token}`, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setReportLoading(false); }
  };

  // Pagination now lives inside <DataTable>. That also retires the
  // Rules-of-Hooks workaround this page needed: the hook can no longer be
  // skipped on the loading render, because it belongs to a child component
  // that simply isn't mounted yet rather than to this one.

  if (!project) return <AppLayout><div className="p-10 text-sm text-zinc-500">Loading...</div></AppLayout>;

  const uMap = Object.fromEntries(users.map((u) => [u._id, u.name || u.email]));
  const uById = Object.fromEntries(users.map((u) => [u._id, { id: u._id, name: u.name || u.email, avatar_url: u.avatar_url }]));
  // Only managers are offered as project manager going forward — but if
  // this project's current owner isn't one (e.g. an admin who created it
  // before this restriction existed), keep them selectable here too so the
  // field doesn't just go blank on an existing project.
  const managerOptions = users.filter((u) => u.role === "manager" || u.role === "owner" || u._id === project?.owner_id);
  const done = tasks.filter((t) => t.status === "live").length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  // Assignee filter options: only people actually assigned to a task on THIS
  // project, not the whole roster — an empty-result option is worse than a
  // short list.
  const taskAssigneeOptions = sortByName(
    Object.values(
      tasks.reduce((acc, t) => {
        (t.assignee_ids || [t.assignee_id]).filter(Boolean).forEach((uid) => {
          if (!acc[uid]) acc[uid] = uById[uid] || { id: uid, name: "Unknown" };
        });
        return acc;
      }, {})
    )
  );

  const visibleTasks = tasks.filter((t) => {
    if (taskStatusFilter !== "all" && t.status !== taskStatusFilter) return false;
    if (taskPriorityFilter !== "all" && t.priority !== taskPriorityFilter) return false;
    if (taskAssigneeFilter !== "all" && !(t.assignee_ids || [t.assignee_id]).includes(taskAssigneeFilter)) return false;
    if (!matchesSchedule(taskScheduleFilter, {
      eta: t.eta, revised: t.revised_eta, done: taskDone(t), completedAt: t.completed_at,
    })) return false;
    if (taskEtaFrom || taskEtaTo) {
      const target = targetDate(t.revised_eta, t.eta);
      if (!target) return false;
      // Bare "yyyy-mm-dd" either way, so a full timestamptz value compares
      // correctly against a plain <input type="date"> boundary.
      const day = String(target).slice(0, 10);
      if (taskEtaFrom && day < taskEtaFrom) return false;
      if (taskEtaTo && day > taskEtaTo) return false;
    }
    const people = (t.assignee_ids || [t.assignee_id]).filter(Boolean).map((uid) => uMap[uid]);
    return matchesQuery(taskQuery, t.title, t.description, t.requested_by, ...people);
  });

  return (
    <AppLayout>
      <div className="sticky top-0 z-20 px-8 py-4 trk-sticky-header">
        <Link
          to="/projects"
          data-testid="project-back-button"
          className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-md border border-[#0176D3] bg-[#EFF6FF] text-sm font-semibold text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-colors"
        >
          <ArrowLeftIcon size={18} weight="bold" /> Back to Projects
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="trk-uppercase-label">Project</div>
            <h1 className="text-3xl font-extrabold tracking-tight break-words font-display">{project.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIssueOpen(true)}
              data-testid="report-issue-project-detail"
              variant="outline"
              className="rounded-sm text-amber-700 border-amber-300 hover:bg-amber-50"
            >
              <WarningCircleIcon size={14} weight="fill" className="mr-1" /> Report Issue
            </Button>
            <Button onClick={checkSkillCoverage} disabled={gapsLoading} data-testid="check-skill-coverage" variant="outline" className="rounded-sm">
              <ShieldWarningIcon size={14} className="mr-1" weight="fill" /> {gapsLoading ? "Analysing…" : "Check Skill Coverage"}
            </Button>
            <Button onClick={generateReport} disabled={reportLoading} data-testid="generate-project-report" className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white">
              <SparkleIcon size={14} className="mr-1" weight="fill" /> {reportLoading ? "Generating..." : "Click here to generate a detailed report"}
            </Button>
            {(isAdmin || isOwner) && (
              <>
                <div className="w-px h-6 bg-border mx-1" />
                <Button
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={deleting}
                  data-testid="delete-project-button"
                  variant="outline"
                  className="rounded-sm text-red-700 border-red-300 hover:bg-red-50"
                >
                  <TrashIcon size={14} className="mr-1" weight="fill" /> {deleting ? "Deleting..." : "Delete Project"}
                </Button>
              </>
            )}
          </div>
        </div>

        <ReportIssueModal
          open={issueOpen}
          onOpenChange={setIssueOpen}
          entityType="project"
          entityId={id}
          entityLabel={project.title}
        />

        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Delete project</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Delete <span className="font-semibold text-foreground">"{project.title}"</span>?
              Its tasks, documents, and members go with it. This cannot be undone.
            </p>
            <DialogFooter>
              <Button
                onClick={deleteProject}
                disabled={deleting}
                data-testid="confirm-delete-project"
                className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? "Deleting..." : "Delete Project"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <div className="flex flex-wrap items-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <StatusBadge status={project.status} />
            <PriorityBadge priority={project.priority} />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="trk-uppercase-label">Progress</span>
            <div className="w-32 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#0176D3]" style={{ width: `${progress}%` }} />
            </div>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="font-mono text-xs text-zinc-600">
            {done}/{tasks.length} tasks live
          </div>
          <div className="text-xs text-zinc-600">
            <span className="mr-1 trk-uppercase-label">Manager</span>
            <span className="font-medium text-[#0F172A]">{uMap[project.owner_id] || "Unassigned"}</span>
          </div>
          {project.end_date && (
            <div className="text-xs text-zinc-600">
              <span className="mr-1 trk-uppercase-label">ETA</span>
              <span className="font-mono">{new Date(project.end_date).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-8 py-6 trk-fade-in">
        {/* Skill Coverage Banner */}
        {skillGaps && (
          <div data-testid="skill-coverage-banner" className={`border rounded-sm mb-6 overflow-hidden ${
            (skillGaps.gaps || []).length > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"
          }`}>
            <div className="flex items-start justify-between p-4">
              <div className="flex items-start gap-3">
                <ShieldWarningIcon size={20} weight="fill" className={(skillGaps.gaps || []).length > 0 ? "text-amber-700" : "text-emerald-700"} />
                <div>
                  <div className="font-bold tracking-tight font-display">
                    {(skillGaps.gaps || []).length > 0
                      ? `${skillGaps.gaps.length} skill gap${skillGaps.gaps.length > 1 ? "s" : ""} detected`
                      : "Team fully covers required skills"}
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    {(skillGaps.required_skills || []).length} skills required · {skillGaps.team_size} assigned member{skillGaps.team_size === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <button onClick={() => setSkillGaps(null)} data-testid="dismiss-skill-gaps" className="text-zinc-500 hover:text-zinc-900"><XIcon size={14} /></button>
            </div>
            {(skillGaps.required_skills || []).length > 0 && (
              <div className="border-t divide-y border-amber-200/60 bg-white/60 divide-amber-200/60">
                {skillGaps.required_skills.map((r, i) => (
                  <div key={i} data-testid={`skill-req-${i}`} className="flex items-start justify-between gap-3 px-4 py-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.skill}</span>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">needs {r.needed_level}</span>
                        {r.covered ? (
                          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 rounded-sm">covered</span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-800 border border-amber-300 bg-amber-100 px-1.5 py-0.5 rounded-sm">gap</span>
                        )}
                      </div>
                      {r.evidence && (
                        <div className="text-[11px] text-zinc-600 italic mt-0.5">"{r.evidence}"</div>
                      )}
                    </div>
                    <div className="text-[11px] text-right text-zinc-600 min-w-[140px]">
                      {r.covered
                        ? (r.covered_by || []).join(", ")
                        : (r.current_best
                            ? <>Best on team: <span className="font-mono">{r.current_best}</span></>
                            : <span className="font-mono text-amber-800">no one has this</span>)}
                    </div>
                    {!r.covered && (
                      <GapAssignButton
                        gap={r}
                        users={users}
                        projectId={id}
                        onAssigned={() => toast.success("Added to onboarding path")}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <Tabs defaultValue="tasks">
          <TabsList className="rounded-sm">
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
            <TabsTrigger value="plan" data-testid="tab-plan">Plan & Details</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
          </TabsList>

          {/* Tasks */}
          <TabsContent value="tasks">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-display">Tasks</h2>
              <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-task-in-project" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                    <PlusIcon size={14} weight="bold" className="mr-1" /> New Task
                  </Button>
                </DialogTrigger>
                {/*
                    Two columns and capped at the viewport.

                    This started as a single narrow column, which was fine for
                    six short fields. It now collects nine, two of which are
                    scrollable people lists, and stacked in max-w-lg the dialog
                    grew taller than the screen with its Create button pushed
                    off the bottom -- unreachable, since a dialog does not
                    scroll the page behind it.

                    max-h-[85vh] + overflow-y-auto is the safety net (the
                    subtask dialog already had it), but the real fix is the
                    layout: the two tall lists sit SIDE BY SIDE below, which
                    halves the height that actually matters.
                */}
                {/*
                    Header / scrolling body / footer, as three flex rows.

                    The previous attempt scrolled the WHOLE dialog and pinned
                    the footer with `sticky bottom-0`. Sticky inside the scroll
                    container does keep the button on screen, but it does it by
                    OVERLAYING the content: the bar sat on top of the people
                    lists, cutting rows in half, and the negative bottom margin
                    left a strip beneath it where the scrolled list showed
                    through. It looked broken because it was covering things.

                    A flex column fixes it properly. Only the middle row
                    scrolls, so the footer is a real row that content stops
                    above rather than passing under. p-0/gap-0 undo
                    DialogContent's own p-6 and gap-4 so each row owns its
                    padding and the divider can run edge to edge.
                */}
                <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0">
                  <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
                    <DialogTitle className="font-display">New Task</DialogTitle>
                  </DialogHeader>
                  <div className="flex-1 min-h-0 px-6 pb-16 space-y-3 overflow-y-auto">
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">Title</Label>
                      <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className="rounded-sm" data-testid="task-title-input" />
                    </div>
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">Description</Label>
                      <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} className="rounded-sm" rows={2} data-testid="task-description-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="block mb-2 trk-uppercase-label">Status</Label>
                        <Select value={taskForm.status} onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{sortByLabel(T_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="block mb-2 trk-uppercase-label">Priority</Label>
                        <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                          <SelectTrigger className="rounded-sm" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{sortStrings(T_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="block mb-2 trk-uppercase-label">
                          ETA <span className="text-red-600">*</span>
                        </Label>
                        <Input type="date" value={taskForm.eta} onChange={(e) => setTaskForm({ ...taskForm, eta: e.target.value })} className="rounded-sm" data-testid="task-eta-input" />
                        {/* Said before the fact, not after a rejected save:
                            this date cannot be edited later. */}
                        <div className="text-[10px] font-mono text-zinc-500 mt-1 leading-tight">
                          Fixed once saved — later changes go in Revised ETA.
                        </div>
                      </div>
                      <div>
                        <Label className="block mb-2 trk-uppercase-label">Revised ETA</Label>
                        <Input type="date" value={taskForm.revised_eta} onChange={(e) => setTaskForm({ ...taskForm, revised_eta: e.target.value })} className="rounded-sm" data-testid="task-revised-eta-input" />
                      </div>
                    </div>
                    {/* The two scrollable lists, side by side. Vertically
                        stacked they were most of the dialog's height. */}
                    <div className="grid items-start grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">
                        Requested by <span className="text-red-600">*</span>
                      </Label>
                      {/* This project's people only, same set Assignees
                          offers — requested-by and assignee are both scoped
                          to the project, not the whole directory. Free text
                          is still available for a requester who isn't on
                          the project at all. */}
                      <RequesterPicker
                        users={projectPeople}
                        value={taskForm.requested_by}
                        onChange={(v) => setTaskForm({ ...taskForm, requested_by: v })}
                        testId="task-requested-by"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="trk-uppercase-label">
                          Assignees <span className="text-red-600">*</span>
                          {taskForm.assignee_ids.length > 0 && ` (${taskForm.assignee_ids.length})`}
                        </Label>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">
                          {projectPeople.length} on project
                        </span>
                      </div>
                      {projectPeople.length > 0 && (
                        <div className="relative mb-2">
                          <MagnifyingGlassIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                          <Input
                            value={assigneeQuery}
                            onChange={(e) => setAssigneeQuery(e.target.value)}
                            placeholder="Search project members…"
                            data-testid="task-assignee-search"
                            className="pl-8 text-sm rounded-sm h-9"
                          />
                        </div>
                      )}
                      {/* Multiple people can be assigned to one task — a
                          plain Select only ever holds one value, so this is
                          a checklist instead. */}
                      <div className="overflow-y-auto border divide-y rounded-sm border-border max-h-32 divide-border" data-testid="task-assignee-list">
                        {projectPeople.length === 0 && (
                          <div className="px-3 py-4 text-xs text-zinc-500" data-testid="task-assignee-empty">
                            Nobody is on this project yet. Add people on the <span className="font-medium">Members</span> tab first —
                            a task cannot be created without an assignee, and only this project's people can be given one.
                          </div>
                        )}
                        {projectPeople.length > 0 && assigneeMatches.length === 0 && (
                          <div className="px-3 py-4 text-xs text-zinc-500" data-testid="task-assignee-no-match">
                            No project member matches “{assigneeQuery.trim()}”.
                          </div>
                        )}
                        {assigneeMatches.map((u) => {
                          const checked = taskForm.assignee_ids.includes(u._id);
                          return (
                            <button
                              type="button"
                              key={u._id}
                              onClick={() => toggleTaskAssignee(u._id)}
                              data-testid={`task-assignee-option-${u._id}`}
                              className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${checked ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
                            >
                              <span>{u.name} <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1">{roleLabel(u.role)}</span></span>
                              {checked && <CheckCircleIcon size={16} weight="fill" color="#0176D3" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    </div>
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">Attachment URL</Label>
                      <Input type="url" value={taskForm.attachment_url} onChange={(e) => setTaskForm({ ...taskForm, attachment_url: e.target.value })} placeholder="https://…" className="rounded-sm" data-testid="task-attachment-input" />
                    </div>
                  </div>
                  {/*
                      JUST THE BUTTON is pinned — there is no footer row.

                      A full-width bar with a divider was too much furniture for
                      one control: it read as a heavy panel bolted to the bottom
                      of the form. This is the same compact blue box as the New
                      Task button that opens the dialog, floated in the corner.

                      Positioned `absolute` with NO `relative` added to
                      DialogContent — that element is already `fixed`, which
                      makes it the containing block, and adding `relative` would
                      have twMerge replace `fixed` and break the dialog's
                      centring. The scroll area's pb-16 keeps the last field
                      from ending underneath it, and the shadow lifts it off
                      whatever it floats over.
                  */}
                  <div className="absolute z-10 bottom-4 right-6">
                    {/* Every requirement in one place, so the button explains
                        itself instead of failing on click. */}
                    <Button
                      onClick={createTask}
                      disabled={taskSubmitting || !taskForm.title.trim() || taskForm.assignee_ids.length === 0
                        || !taskForm.eta || !taskForm.requested_by.trim()}
                      data-testid="save-task-button"
                      className="rounded-sm px-6 bg-[#0176D3] hover:bg-[#005FB2] shadow-lg"
                    >
                      {taskSubmitting ? "Creating…" : "Create"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Same filter set as the global Tasks page (status/priority/
                assignee/schedule/search), scoped to just this project's own
                tasks — no Project filter needed since that's already fixed. */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Select value={taskAssigneeFilter} onValueChange={setTaskAssigneeFilter}>
                <SelectTrigger className="w-full rounded-sm sm:w-44" data-testid="project-task-filter-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All assignees</SelectItem>
                  {taskAssigneeOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.id === currentUser?.id ? `${u.name} (me)` : u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={taskScheduleFilter} onValueChange={setTaskScheduleFilter}>
                <SelectTrigger className="w-full rounded-sm sm:w-52" data-testid="project-task-filter-schedule"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHEDULE_FILTERS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={taskPriorityFilter} onValueChange={setTaskPriorityFilter}>
                <SelectTrigger className="w-full rounded-sm sm:w-36" data-testid="project-task-filter-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {sortStrings(T_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={taskStatusFilter} onValueChange={setTaskStatusFilter}>
                <SelectTrigger className="w-full rounded-sm sm:w-36" data-testid="project-task-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {sortByLabel(T_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <DataTable
              title="Tasks"
              storageKey="projectTasks.visibleColumns"
              testIdPrefix="task"
              toolbarStart={
                <div className="flex items-center gap-2 flex-wrap">
                  <TableSearch
                    value={taskQuery}
                    onChange={setTaskQuery}
                    placeholder="Search this project's tasks…"
                    label="Search tasks"
                    testid="project-tasks-search"
                  />
                  {/* ETA/revised-ETA window — see the state comment above. */}
                  <div className="flex items-center gap-1">
                    <input
                      type="date"
                      value={taskEtaFrom}
                      onChange={(e) => setTaskEtaFrom(e.target.value)}
                      aria-label="ETA/revised ETA from"
                      data-testid="project-tasks-eta-from"
                      className="h-8 rounded-sm border border-border px-2 text-xs text-zinc-700 focus:border-[#0176D3] focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30"
                    />
                    <span className="text-xs text-zinc-400">–</span>
                    <input
                      type="date"
                      value={taskEtaTo}
                      onChange={(e) => setTaskEtaTo(e.target.value)}
                      aria-label="ETA/revised ETA to"
                      data-testid="project-tasks-eta-to"
                      min={taskEtaFrom || undefined}
                      className="h-8 rounded-sm border border-border px-2 text-xs text-zinc-700 focus:border-[#0176D3] focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30"
                    />
                    {(taskEtaFrom || taskEtaTo) && (
                      <button
                        type="button"
                        onClick={() => { setTaskEtaFrom(""); setTaskEtaTo(""); }}
                        data-testid="project-tasks-eta-clear"
                        className="text-xs text-zinc-400 hover:text-zinc-700 px-1"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              }
              rows={visibleTasks}
              rowKey={(t) => t._id}
              /* Tell the task page where it was opened from, so its back link
                 returns to THIS project's Tasks tab instead of the global grid
                 of every task in the company. */
              onRowClick={(t) => nav(`/tasks/${t._id}`, {
                state: { from: { pathname: `/projects/${id}`, label: project?.title || "Project" } },
              })}
              pageSize={20}
              emptyMessage={tasks.length === 0 ? "No tasks yet." : "No tasks match this filter."}
              columns={[
                {
                  key: "task", label: "Task", locked: true, cls: "px-4",
                  cell: (t) => (
                    <div className="min-w-0">
                      <div className="font-medium break-words" title={t.title}>{t.title}</div>
                      {t.description && <div className="text-xs break-words text-zinc-500" title={t.description}>{t.description}</div>}
                    </div>
                  ),
                },
                {
                  key: "status", label: "Status", cls: "px-3", weight: 136,
                  cell: (t) => (
                    // stopPropagation, or picking a status also opens the task.
                    <div onClick={(e) => e.stopPropagation()}>
                      <StatusSelectPill
                        status={t.status}
                        statuses={T_STATUSES}
                        disabled={!canEditTask(t)}
                        onChange={(v) => updateTask(t, { status: v })}
                        testid={`task-status-${t._id}`}
                      />
                    </div>
                  ),
                },
                {
                  key: "priority", label: "Priority", cls: "px-3", weight: 104,
                  cell: (t) => <PriorityBadge priority={t.priority} />,
                },
                {
                  key: "assignee", label: "Assignee", cls: "px-3", weight: 116,
                  cell: (t) => {
                    const people = (t.assignee_ids || [t.assignee_id])
                      .filter(Boolean)
                      .map((uid) => uById[uid] || { id: uid, name: "Unknown" });
                    return <AvatarStack people={people} limit={2} />;
                  },
                },
                {
                  key: "requested_by", label: "Requested By", cls: "px-3", weight: 130,
                  cell: (t) => (
                    <span className="break-words text-zinc-600" title={t.requested_by || ""}>
                      {t.requested_by || "—"}
                    </span>
                  ),
                },
                {
                  key: "eta", label: "ETA", cls: "px-3", weight: 104,
                  cell: (t) => <span className="font-mono text-xs text-zinc-600">{fmtDate(t.eta)}</span>,
                },
                {
                  /* Same slip marker the Tasks page uses: a revised ETA that
                     landed past the date the task was committed to is the one
                     derived signal worth reading straight off the grid. */
                  key: "revised_eta", label: "Revised ETA", cls: "px-3", weight: 124,
                  cell: (t) => {
                    const due = t.due_date ? new Date(t.due_date) : null;
                    const revised = t.revised_eta ? new Date(t.revised_eta) : null;
                    const slipped = Boolean(due && revised && revised > due);
                    return (
                      <span
                        className="inline-flex items-center gap-1.5"
                        title={
                          slipped
                            ? `Slipped past its due date of ${due.toLocaleDateString()}${t.revised_eta_reason ? ` — ${t.revised_eta_reason}` : ""}`
                            : t.revised_eta_reason || undefined
                        }
                      >
                        {slipped && <span className="w-1.5 h-1.5 rounded-full bg-zinc-800 shrink-0" />}
                        <span className={`font-mono text-xs ${slipped ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>
                          {fmtDate(t.revised_eta)}
                        </span>
                      </span>
                    );
                  },
                },
                {
                  // Same column, same renderer as the Tasks grid.
                  key: "slippage", label: "Slippage", cls: "px-3 text-right", weight: 124,
                  // First click = most overdue first, same as the Tasks grid.
                  sortDefaultDir: "asc",
                  sortHint: "Click to sort most overdue first",
                  sortValue: (t) => slippageSortValue({
                    eta: t.eta, revised: t.revised_eta,
                    done: DONE_TASK_STATUSES.includes(t.status),
                  }),
                  cell: (t) => (
                    <SlippageCell
                      status={t.status}
                      eta={t.eta}
                      revised={t.revised_eta}
                      completedAt={t.completed_at}
                    />
                  ),
                },
              ]}
            />
          </TabsContent>

          {/* Plan & Details */}
          <TabsContent value="plan">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="p-5 space-y-4 border rounded-sm border-border">
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Title</Label>
                  <Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="rounded-sm" data-testid="edit-project-title" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Description</Label>
                  <Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="rounded-sm" data-testid="edit-project-description" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Status</Label>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="edit-project-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{sortByLabel(P_STATUSES, (s) => STATUS_STYLES[s]?.label || s).map((s) => <SelectItem key={s} value={s}>{STATUS_STYLES[s]?.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Priority</Label>
                    <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="edit-project-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>{sortStrings(P_PRIORITIES).map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Project Manager</Label>
                  <Select value={editForm.owner_id} onValueChange={(v) => setEditForm({ ...editForm, owner_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="edit-project-manager"><SelectValue placeholder="Select project manager" /></SelectTrigger>
                    <SelectContent>
                      {managerOptions.map((u) => <SelectItem key={u._id} value={u._id}>{u.name}{u.role !== "manager" && u.role !== "owner" ? ` — ${roleLabel(u.role).toUpperCase()}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Start Date</Label>
                    <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="rounded-sm" data-testid="edit-project-start-date" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">ETA</Label>
                    <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="rounded-sm" data-testid="edit-project-eta" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Revised ETA</Label>
                    <Input type="date" value={editForm.revised_eta} onChange={(e) => setEditForm({ ...editForm, revised_eta: e.target.value })} className="rounded-sm" data-testid="edit-project-revised-eta" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Entity</Label>
                    <Input value={editForm.entity} onChange={(e) => setEditForm({ ...editForm, entity: e.target.value })} className="rounded-sm" data-testid="edit-project-entity" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Task Number</Label>
                    <Input value={editForm.task_number} onChange={(e) => setEditForm({ ...editForm, task_number: e.target.value })} className="rounded-sm" data-testid="edit-project-task-number" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Tech Team</Label>
                    <Input value={editForm.tech_team} onChange={(e) => setEditForm({ ...editForm, tech_team: e.target.value })} className="rounded-sm" data-testid="edit-project-tech-team" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Func Team</Label>
                    <Input value={editForm.func_team} onChange={(e) => setEditForm({ ...editForm, func_team: e.target.value })} className="rounded-sm" data-testid="edit-project-func-team" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">FY Lic Cost $</Label>
                    <Input type="number" step="0.01" value={editForm.fy_lic_cost} onChange={(e) => setEditForm({ ...editForm, fy_lic_cost: e.target.value })} className="rounded-sm" data-testid="edit-project-fy-lic-cost" />
                  </div>
                  <div>
                    <Label className="block mb-2 trk-uppercase-label">Imp Cost $</Label>
                    <Input type="number" step="0.01" value={editForm.imp_cost} onChange={(e) => setEditForm({ ...editForm, imp_cost: e.target.value })} className="rounded-sm" data-testid="edit-project-imp-cost" />
                  </div>
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Risk / Challenges</Label>
                  <Textarea value={editForm.risk_challenges} onChange={(e) => setEditForm({ ...editForm, risk_challenges: e.target.value })} rows={2} className="rounded-sm" data-testid="edit-project-risk" />
                </div>
                <div>
                  <Label className="block mb-2 trk-uppercase-label">Comments</Label>
                  <Textarea value={editForm.comments} onChange={(e) => setEditForm({ ...editForm, comments: e.target.value })} rows={2} className="rounded-sm" data-testid="edit-project-comments" />
                </div>
                <Button onClick={saveProject} data-testid="save-project-edit" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Save</Button>
              </div>
              <div className="p-5 border rounded-sm border-border">
                <Label className="block mb-2 trk-uppercase-label">Plan</Label>
                <Textarea value={editForm.plan} onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })} rows={12} className="font-mono text-xs rounded-sm" data-testid="edit-project-plan" placeholder="Write your project plan here — objectives, milestones, timelines..." />
              </div>
            </div>
          </TabsContent>

          {/* Docs */}
          <TabsContent value="docs">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-display">Related documents</h2>
              <Dialog open={docOpen} onOpenChange={setDocOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-document-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                    <LinkSimpleIcon size={14} className="mr-1" /> Add link
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md rounded-sm">
                  <DialogHeader><DialogTitle className="font-display">Add document link</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">Title</Label>
                      <Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} className="rounded-sm" data-testid="doc-title-input" />
                    </div>
                    <div>
                      <Label className="block mb-2 trk-uppercase-label">URL</Label>
                      <Input value={docForm.url} onChange={(e) => setDocForm({ ...docForm, url: e.target.value })} placeholder="https://..." className="rounded-sm" data-testid="doc-url-input" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={addDoc} data-testid="save-doc-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">Add</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            {(project.document_links || []).length === 0 ? (
              <div className="p-10 text-sm text-center border border-dashed rounded-sm border-border text-zinc-500">
                No documents yet. Attach URLs to specs, designs, meeting notes.
              </div>
            ) : (
              <div className="overflow-hidden border rounded-sm border-border">
                {project.document_links.map((d, idx) => (
                  <div key={idx} className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0">
                    <div className="min-w-0">
                      {/* The title is plain text; the address under it is the
                          blue hyperlink (explicit request). It's shown shortened
                          by prettyUrl — a pasted SharePoint URL is 300
                          characters of percent-encoding — with the full URL on
                          href and on hover. */}
                      {d.title && <div className="font-medium text-[#0F172A]">{d.title}</div>}
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noreferrer"
                        title={d.url}
                        data-testid={`doc-link-${idx}`}
                        className="block text-sm truncate text-[#0176D3] underline underline-offset-2 hover:text-[#005FB2]"
                      >
                        {prettyUrl(d.url)}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={d.url} target="_blank" rel="noreferrer" className="p-2 border rounded-sm hover:bg-zinc-50 border-border" data-testid={`doc-open-${idx}`}>
                        <ArrowSquareOutIcon size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Members — who can see this project and its tasks. The owner
              always has access (see "read projects"/"read tasks" RLS) and
              is shown here for clarity but isn't a removable row. */}
          <TabsContent value="members">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-display">Members</h2>
              {canManageMembers && (
                <div className="flex items-center gap-2">
                  <Select value={addMemberId} onValueChange={setAddMemberId}>
                    <SelectTrigger className="w-56 rounded-sm" data-testid="add-member-select">
                      <SelectValue placeholder="Add a member…" />
                    </SelectTrigger>
                    <SelectContent>
                      {addableUsers.map((u) => <SelectItem key={u._id} value={u._id}>{u.name} — {roleLabel(u.role).toUpperCase()}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={addMember} disabled={!addMemberId} data-testid="add-member-button" className="rounded-sm bg-[#0176D3] hover:bg-[#005FB2]">
                    <PlusIcon size={14} weight="bold" className="mr-1" /> Add
                  </Button>
                </div>
              )}
            </div>

            <div className="overflow-hidden border rounded-sm border-border">
              <div className="divide-y divide-border">
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-50/60">
                  <div className="min-w-0">
                    <div className="font-medium">{uMap[project.owner_id] || "Unknown"}</div>
                    <div className="text-xs text-zinc-500">Project Manager</div>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Can't be removed</span>
                </div>
                {members.length === 0 ? (
                  <div className="px-4 py-8 text-sm text-center text-zinc-500">
                    No other members yet. {canManageMembers && "Add someone above so they can see this project."}
                  </div>
                ) : (
                  <PagedList items={members} pageSize={15} testIdPrefix="project-members" className="px-4 pb-3">
                    {(rows) => (
                      <div className="-mx-4">
                  {rows.map((m) => (
                    <div key={m.user_id} className="flex items-center justify-between px-4 py-3" data-testid={`member-row-${m.user_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium">{m.name}</div>
                        <div className="text-xs text-zinc-500">{m.email} · {roleLabel(m.role).toUpperCase()}</div>
                      </div>
                    </div>
                  ))}
                      </div>
                    )}
                  </PagedList>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
